/**
 * Plans: generate one from a prompt, browse it, schedule it, and the crew feed.
 *
 * Generation needs the network and takes 30-60 seconds, so the UI says so
 * plainly rather than showing an ambiguous spinner that reads as "hung".
 */

import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'

import { api, ApiError } from '../api/client'
// Shared with the Log screen, which caches these in Dexie so a planned workout
// can be started with no signal. One definition, or the two drift.
import type { Exercise, Plan } from '../db'
import { unitFor, useDistanceOverrides } from '../lib/distance'
import { getActiveSession, getExercises } from '../db/queries'
import { startSession } from '../db/mutations'
import { refreshPlans, syncSoon } from '../sync/engine'
import { formatTarget } from '../lib/planTarget'
import { startedAtFor } from '../lib/day'
import { PlanBuilder } from '../components/PlanBuilder'
import { useReadiness, type Readiness } from '../components/Readiness'
import { ExerciseInfoPanel, hasInfo } from '../components/ExerciseInfo'
import { Calendar, RESERVED_TITLE, type ScheduleRow, type SessionRow }
  from '../components/Calendar'
import { Scheduler } from '../components/Scheduler'
import { OneOffPlanner } from '../components/OneOffPlanner'
import { formatWeight, type DistancePref, type Unit } from '../lib/units'

type FeedRow = {
  user_id: number
  name: string
  is_me: boolean
  sessions_this_week: number
  last_session_at: string | null
  prs: { exercise: string; achieved_at: string | null }[]
}

const EQUIPMENT = [
  'barbell', 'dumbbell', 'rack', 'bench', 'pull-up bar',
  'cable machine', 'machines', 'kettlebell', 'bands', 'bodyweight only',
]

type CalendarData = {
  schedule: ScheduleRow[]
  sessions: SessionRow[]
  today: string
}

export function PlanScreen({
  unit,
  distancePref,
  onStarted,
}: {
  unit: Unit
  distancePref: DistancePref
  /** Switch to the Log tab — starting a workout should land you where you log it. */
  onStarted?: () => void
}) {
  const [plans, setPlans] = useState<Plan[] | null>(null)
  const [openId, setOpenId] = useState<number | null>(null)
  const [composing, setComposing] = useState<'ai' | 'manual' | null>(null)
  const [editing, setEditing] = useState<Plan | null>(null)
  const [feed, setFeed] = useState<FeedRow[] | null>(null)

  // From the Dexie cache, so the picker in the builder is the same list the
  // log screen uses and works with no connection.
  const exercises = useLiveQuery(getExercises, [], [] as Exercise[])

  const [calendar, setCalendar] = useState<CalendarData | null>(null)
  // Which day the one-off planner is open for, if any.
  const [planningDate, setPlanningDate] = useState<string | null>(null)

  const load = () => {
    api<{ plans: Plan[] }>('/plans')
      .then((r) => {
        setPlans(r.plans)
        // Mirror into Dexie on the way past. A plan generated at home is no use
        // if the Log screen can't find it in a basement gym with no signal.
        void refreshPlans(r.plans)
      })
      .catch(() => setPlans(null))
  }

  // One-offs ride in the same response so they reach the Dexie cache, but they
  // are single scheduled workouts, not programmes — listing them here would
  // bury the plan you're actually running under every ad-hoc session.
  const programmes = plans?.filter((p) => p.source !== 'oneoff') ?? null

  const loadCalendar = () => {
    api<CalendarData>('/schedule?back=120&ahead=120')
      .then(setCalendar)
      .catch(() => setCalendar(null))
  }

  useEffect(load, [])
  useEffect(loadCalendar, [])
  useEffect(() => {
    api<{ feed: FeedRow[] }>('/feed').then((r) => setFeed(r.feed)).catch(() => setFeed(null))
  }, [])

  return (
    <>
      <h1>Plans</h1>

      {calendar && (
        <Calendar
          schedule={calendar.schedule}
          sessions={calendar.sessions}
          today={calendar.today}
          onLogDay={async (date, row) => {
            // One workout at a time. Starting a second while one is open would
            // leave the first orphaned and unfinishable — the log screen only
            // ever shows the newest.
            const active = await getActiveSession()
            if (!active) {
              // Opening from the calendar links the session to the slot it came
              // from, which is what lets the server settle it as completed
              // later. A past date is backdated rather than refused.
              await startSession({
                title: row?.plan_day_name ?? undefined,
                plan_id: row?.plan_id,
                plan_day_id: row?.plan_day_id,
                started_at: startedAtFor(date),
              })
              syncSoon()
            }
            onStarted?.()
          }}
          onPlanDay={setPlanningDate}
          // Editing one slot at a time. Every one of these refetches rather
          // than patching local state: the server settles slots on read (a
          // moved day can come back completed by a session already logged
          // against it), so what it returns is the only version worth showing.
          onRemovePlanned={async (row) => {
            await api(`/schedule/${row.id}`, { method: 'DELETE' })
            loadCalendar()
            // A removed one-off leaves an archived plan behind, and the Log
            // screen starts workouts from the Dexie mirror — without this it
            // would keep offering a workout that is no longer on any day.
            void refreshPlans()
          }}
          onMovePlanned={async (row, date) => {
            await api(`/schedule/${row.id}`, { method: 'PATCH', body: { date } })
            loadCalendar()
          }}
          // One tap, no form. It's the empty one-off the planner can already
          // save, with the name filled in — so it starts, gets claimed by
          // whatever you actually do that day, and moves and removes like any
          // other square. Nothing new server-side.
          onReserveDay={async (date) => {
            await api('/schedule/oneoff', {
              body: { date, title: RESERVED_TITLE, items: [] },
            })
            loadCalendar()
            // It's a plan in the database, so the Log screen needs it mirrored
            // to start the day with no signal.
            void refreshPlans()
          }}
          onRestorePlanned={async (row) => {
            await api('/schedule/slot', {
              body: {
                plan_id: row.plan_id,
                plan_day_id: row.plan_day_id,
                date: row.scheduled_date,
              },
            })
            loadCalendar()
            void refreshPlans()
          }}
        />
      )}

      {planningDate && (
        <OneOffPlanner
          date={planningDate}
          exercises={exercises}
          unit={unit}
          distancePref={distancePref}
          onSaved={() => {
            setPlanningDate(null)
            loadCalendar()
            // The one-off is a plan server-side, so the local mirror needs it
            // too — that's where the Log screen reads the prescription from
            // when you start the workout with no signal.
            void refreshPlans()
          }}
          onCancel={() => setPlanningDate(null)}
        />
      )}

      {composing === 'ai' && (
        <PlanComposer
          unit={unit}
          onDone={(plan) => {
            setComposing(null)
            setOpenId(plan.id)
            load()
          }}
          onCancel={() => setComposing(null)}
        />
      )}

      {composing === 'manual' && (
        <PlanBuilder
          exercises={exercises}
          unit={unit}
          distancePref={distancePref}
          onDone={(plan) => {
            setComposing(null)
            setOpenId(plan.id)
            load()
          }}
          onCancel={() => setComposing(null)}
        />
      )}

      {editing && (
        <PlanBuilder
          key={editing.id}
          exercises={exercises}
          unit={unit}
          distancePref={distancePref}
          editing={editing}
          onDone={(plan) => {
            setEditing(null)
            setOpenId(plan.id)
            load()
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {composing === null && (
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <button style={{ flex: 1, marginTop: 0 }} onClick={() => setComposing('ai')}>
            Build a plan with AI
          </button>
          <button
            className="secondary"
            style={{ flex: 1, marginTop: 0 }}
            onClick={() => setComposing('manual')}
          >
            Write one myself
          </button>
        </div>
      )}

      {plans === null && <p className="muted">Couldn't load plans — needs a connection.</p>}

      {programmes?.length === 0 && composing === null && (
        <p className="muted" style={{ marginTop: '1rem' }}>
          No plans yet. Describe what you want and Claude will write one, or
          write your own if you already know the programme.
        </p>
      )}

      {programmes?.map((plan) => (
        <div key={plan.id} className="card" style={{ marginTop: '1rem' }}>
          <div
            className="row tappable"
            onClick={() => setOpenId(openId === plan.id ? null : plan.id)}
          >
            <div>
              <strong>{plan.name}</strong>
              <div className="muted">
                {plan.days_per_week}× / week · {plan.weeks} weeks
                {plan.source === 'ai' && ' · AI'}
              </div>
            </div>
            <span className="muted">{openId === plan.id ? '▾' : '▸'}</span>
          </div>

          {openId === plan.id && (
            <PlanDetail
              plan={plan}
              exercises={exercises}
              unit={unit}
              distancePref={distancePref}
              onChanged={load}
              onScheduled={loadCalendar}
              onEdit={() => setEditing(plan)}
            />
          )}
        </div>
      ))}

      {feed && feed.length > 1 && (
        <>
          <h2>This week</h2>
          <div className="card">
            <ul className="plain" style={{ margin: 0 }}>
              {feed.map((row) => (
                <li key={row.user_id}>
                  <span>
                    {row.name}
                    {row.is_me && <span className="muted"> · you</span>}
                    {row.prs.length > 0 && (
                      <div className="muted" style={{ fontSize: '.82rem' }}>
                        PR: {row.prs.map((p) => p.exercise).join(', ')}
                      </div>
                    )}
                  </span>
                  <span className="muted">
                    {row.sessions_this_week === 0
                      ? '—'
                      : `${row.sessions_this_week} session${row.sessions_this_week === 1 ? '' : 's'}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </>
  )
}

function PlanDetail({
  plan,
  exercises,
  unit,
  distancePref,
  onChanged,
  onScheduled,
  onEdit,
}: {
  plan: Plan
  /** The library, from the Dexie cache — a plan item carries only a name and
   *  an id, and the description lives on the exercise. */
  exercises: Exercise[]
  unit: Unit
  distancePref: DistancePref
  onChanged: () => void
  onScheduled: () => void
  onEdit: () => void
}) {
  const [result, setResult] = useState<{ created: number; removed: number } | null>(null)
  // Which item's description is expanded, keyed by plan item rather than by
  // exercise: the same lift can appear on two days of one plan, and opening
  // Monday's squat should not also open Thursday's.
  const [info, setInfo] = useState<number | null>(null)

  const byId = useMemo(
    () => new Map(exercises.map((e) => [e.id, e])),
    [exercises],
  )
  const overrides = useDistanceOverrides()

  const hasRpe = plan.days.some((day) => day.items.some((item) => item.target_rpe))

  return (
    <div style={{ marginTop: '.75rem' }}>
      {plan.description && (
        <p className="muted" style={{ marginTop: 0 }}>{plan.description}</p>
      )}

      {plan.progression && (
        <div
          style={{
            padding: '.6rem .75rem',
            background: 'var(--surface-2)',
            borderRadius: 'var(--radius)',
            fontSize: '.88rem',
            marginBottom: '.75rem',
          }}
        >
          <strong style={{ fontSize: '.82rem' }}>Progression</strong>
          <div style={{ marginTop: '.2rem' }}>{plan.progression}</div>
        </div>
      )}

      {plan.days.map((day) => (
        <div key={day.id} style={{ marginBottom: '.9rem' }}>
          <strong style={{ fontSize: '.95rem' }}>{day.name}</strong>
          <ul className="plain" style={{ margin: '.3rem 0 0' }}>
            {day.items.map((item) => {
              // Tapping the row opens "what is this", the same gesture and the
              // same chevron as the plan card above it. Rows with nothing to
              // say — an exercise the user invented, or one the server couldn't
              // resolve — stay inert rather than opening an empty box.
              const full = item.exercise ? byId.get(item.exercise.id) : undefined
              const expandable = hasInfo(full)
              const open = expandable && info === item.id

              return (
                <li key={item.id} style={{ display: 'block' }}>
                  <div
                    className={expandable ? 'row tappable' : 'row'}
                    aria-expanded={expandable ? open : undefined}
                    onClick={expandable
                      ? () => setInfo(open ? null : item.id)
                      : undefined}
                  >
                    <span>
                      {item.superset_group && (
                        <span className="muted">{item.superset_group} · </span>
                      )}
                      {item.exercise?.name ?? '—'}
                      {expandable && (
                        <span className="muted"> {open ? '▾' : '▸'}</span>
                      )}
                    </span>
                    <span className="muted">
                      {formatTarget(item,
                                    unitFor(byId.get(item.exercise?.id ?? -1),
                                            { unit, pref: distancePref, overrides }))}
                    </span>
                  </div>
                  {item.notes && (
                    <div className="muted" style={{ fontSize: '.8rem', marginTop: '.1rem' }}>
                      {item.notes}
                    </div>
                  )}
                  {open && full && <ExerciseInfoPanel exercise={full} />}
                </li>
              )
            })}
          </ul>
        </div>
      ))}

      {/* A bare "@7" is powerlifting shorthand, and nothing else on the screen
          says what it measures — the first read is usually "70%?". It isn't a
          percentage: one percentage can't be right across a rep range, which
          is the whole reason the prescription is an effort level. Only shown
          when something actually carries an RPE. */}
      {hasRpe && (
        <p className="muted" style={{ fontSize: '.8rem' }}>
          <strong>RPE</strong> is how hard the set should feel, not a percentage
          of your max: RPE 7 means about 3 reps left in the tank, RPE 9 about 1.
          Pick a weight that lands there for the rep range.
        </p>
      )}

      {result !== null ? (
        <p className="muted">
          {result.created > 0
            ? `Scheduled ${result.created} session${result.created === 1 ? '' : 's'} —`
              + " they're on the calendar at the top of this tab."
            : result.removed > 0
              ? `Cleared ${result.removed} upcoming day${result.removed === 1 ? '' : 's'}.`
                + " Workouts you've already done are untouched."
              // Nothing was removed, so don't claim anything was. This plan
              // simply wasn't on the calendar — and if there are still squares
              // up there, they belong to another plan, which is now named
              // under each one in the day detail.
              : 'This plan had nothing upcoming to clear. Any days still on the'
                + ' calendar belong to another plan — tap the day to see which,'
                + ' or remove it there.'}
        </p>
      ) : (
        <Scheduler
          plan={plan}
          onScheduled={(created, removed) => {
            setResult({ created, removed })
            onScheduled()
          }}
        />
      )}

      <p className="muted" style={{ fontSize: '.8rem', marginBottom: 0 }}>
        Scheduling is what makes adherence measurable — without a record of what
        was supposed to happen, nothing can tell you that you skipped it.
      </p>

      <div style={{ display: 'flex', gap: '.5rem' }}>
        <button
          className="secondary"
          style={{ marginTop: '.5rem', fontSize: '.85rem', padding: '.3rem .7rem',
                   minHeight: 0 }}
          onClick={onEdit}
        >
          Edit plan
        </button>
        <button
          className="secondary"
          style={{ marginTop: '.5rem', fontSize: '.85rem', padding: '.3rem .7rem',
                   minHeight: 0 }}
          onClick={async () => {
            await api(`/plans/${plan.id}`, { method: 'DELETE' })
            onChanged()
          }}
        >
          Archive plan
        </button>
      </div>
    </div>
  )
}

function PlanComposer({
  unit,
  onDone,
  onCancel,
}: {
  unit: Unit
  onDone: (plan: Plan) => void
  onCancel: () => void
}) {
  const [prompt, setPrompt] = useState('')
  const [days, setDays] = useState(4)
  const [minutes, setMinutes] = useState(60)
  const [experience, setExperience] = useState('intermediate')
  const [equipment, setEquipment] = useState<string[]>(['barbell', 'dumbbell', 'rack', 'bench'])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const readiness = useReadiness()

  const toggle = (item: string) =>
    setEquipment((cur) =>
      cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item])

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <label htmlFor="prompt">What do you want?</label>
      <textarea
        id="prompt"
        rows={3}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Get stronger on squat and bench. Home gym, coming back after 3 months off."
        style={{
          width: '100%', padding: '.65rem .7rem', font: 'inherit',
          borderRadius: 'var(--radius)', border: '1px solid var(--border)',
          background: 'var(--surface)', color: 'var(--text)', resize: 'vertical',
        }}
      />

      <div style={{ display: 'flex', gap: '.75rem' }}>
        <div style={{ flex: 1 }}>
          <label htmlFor="days">Days / week</label>
          <input id="days" type="number" min={1} max={7} value={days}
                 onChange={(e) => setDays(Number(e.target.value))} />
        </div>
        <div style={{ flex: 1 }}>
          <label htmlFor="mins">Minutes / session</label>
          <input id="mins" type="number" min={20} max={180} step={5} value={minutes}
                 onChange={(e) => setMinutes(Number(e.target.value))} />
        </div>
      </div>

      <label htmlFor="exp">Experience</label>
      <select id="exp" value={experience} onChange={(e) => setExperience(e.target.value)}>
        <option value="beginner">Beginner</option>
        <option value="intermediate">Intermediate</option>
        <option value="advanced">Advanced</option>
      </select>

      <label>Equipment</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
        {EQUIPMENT.map((item) => (
          <button
            key={item}
            type="button"
            className={equipment.includes(item) ? '' : 'secondary'}
            style={{ marginTop: 0, padding: '.3rem .7rem', minHeight: 0, fontSize: '.85rem' }}
            onClick={() => toggle(item)}
          >
            {item}
          </button>
        ))}
      </div>

      <BodyWeightNote readiness={readiness} unit={unit} />
      <BalanceNote readiness={readiness} />

      {error && <div className="error">{error}</div>}

      {busy && (
        <p className="muted" style={{ marginBottom: 0 }}>
          Writing your plan — this takes about half a minute. Claude is picking
          exercises around your equipment and what you've been lifting lately.
        </p>
      )}

      <div style={{ display: 'flex', gap: '.5rem' }}>
        <button className="secondary" style={{ flex: 1 }} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          style={{ flex: 2 }}
          disabled={busy || prompt.trim().length < 10}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              const res = await api<{ plan: Plan }>('/ai/plan', {
                body: {
                  prompt: prompt.trim(),
                  days_per_week: days,
                  session_minutes: minutes,
                  experience,
                  equipment,
                },
              })
              onDone(res.plan)
            } catch (err) {
              setError(
                err instanceof ApiError
                  ? err.message
                  : "Couldn't reach the server. Plan generation needs a connection.",
              )
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? 'Writing…' : 'Generate plan'}
        </button>
      </div>
    </div>
  )
}

/**
 * What the planner knows about your body weight, said before it writes anything.
 *
 * Two jobs. With a weight on file it makes the reasoning visible — a plan that
 * quietly prescribes assisted pull ups to somebody who asked for pull ups should
 * not be a surprise. With no weight on file it asks for one, here, at the moment
 * it would actually be used; a prompt on some settings screen is a prompt nobody
 * ever sees a reason to answer.
 *
 * Silent while loading and silent offline. Generation needs a connection anyway,
 * and the button below says so.
 */
/**
 * The last 28 days in sets per category, and how to make the plan act on them.
 *
 * The numbers, never a verdict: the app does not decide here what counts as
 * neglected, and saying "your legs are behind" would be inventing a judgement
 * the plan will not share — because balance is OPT IN. Claude is sent these
 * counts every time and told to leave them alone unless the request asks for
 * balance, so a plan you asked to be squat-focused stays squat-focused. Hence
 * the second sentence: without it this would be a number with no visible way
 * to use it.
 *
 * Stating the counts cannot drift from what was sent, because it is what was
 * sent — same helper, one call.
 */
function BalanceNote({ readiness }: { readiness: Readiness | null | undefined }) {
  const balance = readiness?.balance
  if (!balance) return null

  return (
    <p className="muted" style={{ fontSize: '.82rem', margin: '.25rem 0 0' }}>
      Your last {balance.window_days} days —{' '}
      {balance.by_category.map((c) => `${c.sets} ${c.category}`).join(', ')}. Ask
      for a balanced plan if you want it to even that out.
    </p>
  )
}

function BodyWeightNote({
  readiness,
  unit,
}: {
  readiness: Readiness | null | undefined
  unit: Unit
}) {
  if (!readiness) return null

  const gated = readiness.movements.filter(
    (m) => m.verdict === 'not_ready' || m.verdict === 'close')

  let text: string
  if (!readiness.bodyweight) {
    text = 'Log your weight on the Progress tab and plans can account for it — '
      + 'a pull up is your whole body weight every rep, so it is the one number '
      + "that decides whether an exercise is hard or impossible."
  } else {
    const bw = formatWeight(readiness.bodyweight.weight_kg, unit)
    text = gated.length > 0
      ? `Using your body weight (${bw}). ${gated.map((m) => m.movement).join(' and ')} `
        + `will come out as the assisted version until you're pulling closer to it.`
      : `Using your body weight (${bw}) when it picks bodyweight movements.`
    if (readiness.bodyweight.is_stale) {
      text += ' That weigh-in is a few months old — worth updating first.'
    }
  }

  return (
    <p className="muted" style={{ fontSize: '.82rem', margin: '.25rem 0 0' }}>
      {text}
    </p>
  )
}
